import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = '/api';

// Strategy dropdown options
const STRATEGY_OPTIONS = ['Naked Call', 'Naked Put', 'Covered Call'];

function TradingJournal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterAccount, setFilterAccount] = useState('');

  // Editing strategy
  const [editingId, setEditingId] = useState(null);

  // ─── Fetch journal entries ───────────────────────────────
  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSymbol) params.symbol = filterSymbol;
      if (filterAccount) params.account = filterAccount;

      const res = await axios.get(`${API_BASE}/journal`, { params });
      setEntries(res.data.entries || []);
      setError('');
    } catch (err) {
      setError('Failed to load journal: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSymbol, filterAccount]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // ─── Update strategy ────────────────────────────────────
  async function handleStrategyChange(id, strategy) {
    try {
      await axios.put(`${API_BASE}/journal/${id}/strategy`, { strategy });
      setEntries(prev => prev.map(e => e.id === id ? { ...e, strategy } : e));
      setEditingId(null);
    } catch (err) {
      alert('Failed to update strategy: ' + (err.response?.data?.error || err.message));
    }
  }

  // ─── Download CSV ───────────────────────────────────────
  function downloadCSV() {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterSymbol) params.set('symbol', filterSymbol);
    if (filterAccount) params.set('account', filterAccount);
    const url = `${API_BASE}/journal/csv?${params.toString()}`;
    window.open(url, '_blank');
  }

  // Manual process button removed in Session 39 — journal is strictly EOD-driven now.

  // ─── Format helpers ─────────────────────────────────────
  function fmt(val, decimals = 2) {
    if (val === null || val === undefined || val === '') return '-';
    const num = parseFloat(val);
    if (isNaN(num)) return '-';
    return num.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }

  function fmtPct(val) {
    if (val === null || val === undefined || val === '') return '-';
    const num = parseFloat(val);
    if (isNaN(num)) return '-';
    return (num * 100).toFixed(2) + '%';
  }

  function fmtDate(dateStr) {
    if (!dateStr) return '-';
    // Convert "2026-05-02" to "02 May 2026"
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  // Get unique symbols and accounts for filter dropdowns.
  // Use `script` (full company name) — `symbol` now holds the NSE ticker (internal match key).
  const uniqueSymbols = [...new Set(entries.map(e => e.script).filter(Boolean))].sort();
  const uniqueAccounts = [...new Set(entries.map(e => e.account).filter(Boolean))].sort();

  // ─── Styles ─────────────────────────────────────────────
  const t = {
    bg: '#0f1117', bgCard: '#181b27', border: '#1e2130', headerBg: '#13151e',
    text: '#e1e4ea', textMuted: '#8b8fa3', textDim: '#4a5568',
    green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  };

  const thStyle = {
    padding: '6px 8px', fontSize: 10, fontWeight: 600, color: t.textMuted,
    textAlign: 'right', borderBottom: `1px solid ${t.border}`,
    position: 'sticky', top: 0, background: t.headerBg, zIndex: 1,
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '5px 8px', fontSize: 11, color: t.text,
    textAlign: 'right', borderBottom: `1px solid ${t.border}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: 16, background: t.bg, minHeight: '100%' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: t.text }}>
          Trading Journal
          <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 400, marginLeft: 8 }}>
            {entries.length} entries
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Filters */}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: 4 }}>
            <option value="">All Status</option>
            <option value="OPEN">Open</option>
            <option value="CLOSED">Closed</option>
          </select>

          <select value={filterSymbol} onChange={e => setFilterSymbol(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: 4 }}>
            <option value="">All Symbols</option>
            {uniqueSymbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 11, background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: 4 }}>
            <option value="">All Accounts</option>
            {uniqueAccounts.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          {/* CSV download — Process Now button removed (Session 39: journal is EOD-driven via 4 PM scheduler) */}
          <button onClick={downloadCSV}
            style={{ padding: '4px 12px', fontSize: 11, fontWeight: 500, background: 'transparent', color: t.textMuted, border: `1px solid ${t.border}`, borderRadius: 4, cursor: 'pointer' }}>
            Download CSV
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div style={{ fontSize: 11, color: t.red, marginBottom: 8 }}>{error}</div>}

      {/* Loading */}
      {loading && <div style={{ fontSize: 12, color: t.textMuted, padding: 20 }}>Loading journal...</div>}

      {/* Empty state */}
      {!loading && entries.length === 0 && (
        <div style={{ fontSize: 12, color: t.textMuted, padding: 40, textAlign: 'center' }}>
          No journal entries yet. Entries are created automatically at 3:32 PM from completed trades.
        </div>
      )}

      {/* Journal table */}
      {!loading && entries.length > 0 && (
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 130px)', borderRadius: 6, border: `1px solid ${t.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: t.bgCard }}>
            <thead>
              <tr>
                {/* Entry side */}
                <th style={{ ...thStyle, textAlign: 'left' }}>Username</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Date</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Strategy</th>
                <th style={thStyle}>Option</th>
                <th style={thStyle}>B/S</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Script</th>
                <th style={thStyle}>Strike</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Expiry</th>
                <th style={thStyle}>CMP Entry</th>
                <th style={thStyle}>Margin/Lot</th>
                <th style={thStyle}>Margin Ded.</th>
                <th style={thStyle}>Prem/Share</th>
                <th style={thStyle}>IV Entry</th>
                <th style={thStyle}>Lot Size</th>
                <th style={thStyle}>Lots</th>
                <th style={thStyle}>Entry Prem.</th>
                {/* Exit side */}
                <th style={{ ...thStyle, borderLeft: `2px solid ${t.border}` }}>Exit Date</th>
                <th style={thStyle}>Prem/Share</th>
                <th style={thStyle}>IV Exit</th>
                <th style={thStyle}>Exit Prem.</th>
                <th style={thStyle}>CMP Exit</th>
                <th style={thStyle}>Margin Rel.</th>
                {/* Performance */}
                <th style={{ ...thStyle, borderLeft: `2px solid ${t.border}` }}>Gross Profit</th>
                <th style={thStyle}>Return %</th>
                <th style={thStyle}>Days</th>
                <th style={thStyle}>XIRR</th>
                <th style={thStyle}>LTP Expiry</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const profit = parseFloat(e.gross_profit);
                const profitColor = !isNaN(profit) ? (profit >= 0 ? t.green : t.red) : t.text;
                const isOpen = e.status === 'OPEN';

                return (
                  <tr key={e.id} style={{ background: isOpen ? 'rgba(59,130,246,0.04)' : 'transparent' }}>
                    {/* Entry side */}
                    <td style={{ ...tdStyle, textAlign: 'left', fontSize: 10 }}>{e.username || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', fontSize: 10 }}>{e.account || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', fontSize: 10 }}>{fmtDate(e.entry_date)}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', padding: '2px 4px' }}>
                      {editingId === e.id ? (
                        <select
                          value={e.strategy}
                          onChange={ev => handleStrategyChange(e.id, ev.target.value)}
                          onBlur={() => setEditingId(null)}
                          autoFocus
                          style={{ padding: '2px 4px', fontSize: 10, background: t.bgCard, color: t.text, border: `1px solid ${t.blue}`, borderRadius: 3, outline: 'none' }}
                        >
                          {STRATEGY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <span
                          onClick={() => setEditingId(e.id)}
                          style={{ cursor: 'pointer', fontSize: 10, padding: '2px 6px', borderRadius: 3, border: `1px solid transparent`, display: 'inline-block' }}
                          title="Click to edit strategy"
                        >
                          {e.strategy}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 10 }}>{e.option_type}</td>
                    <td style={{ ...tdStyle, fontSize: 10, color: e.direction === 'SELL' ? t.red : t.green }}>{e.direction === 'SELL' ? 'S' : 'B'}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.script || e.symbol}</td>
                    <td style={tdStyle}>{fmt(e.strike, 0)}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', fontSize: 10 }}>{fmtDate(e.expiry)}</td>
                    <td style={tdStyle}>{fmt(e.cmp_at_entry)}</td>
                    <td style={tdStyle}>{fmt(e.margin_per_lot)}</td>
                    <td style={tdStyle}>{fmt(e.margin_deducted)}</td>
                    <td style={tdStyle}>{fmt(e.entry_premium_per_share)}</td>
                    <td style={tdStyle}>{fmt(e.iv_at_entry, 1)}</td>
                    <td style={tdStyle}>{e.lot_size || '-'}</td>
                    <td style={tdStyle}>{e.lots_traded || '-'}</td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{fmt(e.entry_premium)}</td>

                    {/* Exit side */}
                    <td style={{ ...tdStyle, borderLeft: `2px solid ${t.border}`, fontSize: 10 }}>
                      {isOpen ? <span style={{ color: t.blue, fontSize: 9, fontWeight: 600 }}>OPEN</span> : fmtDate(e.exit_date)}
                    </td>
                    <td style={tdStyle}>{isOpen ? '-' : fmt(e.exit_premium_per_share)}</td>
                    <td style={tdStyle}>{isOpen ? '-' : fmt(e.iv_at_exit, 1)}</td>
                    <td style={tdStyle}>{isOpen ? '-' : fmt(e.exit_premium)}</td>
                    <td style={tdStyle}>{isOpen ? '-' : fmt(e.cmp_at_exit)}</td>
                    <td style={tdStyle}>{isOpen ? '-' : fmt(e.margin_released)}</td>

                    {/* Performance */}
                    <td style={{ ...tdStyle, borderLeft: `2px solid ${t.border}`, color: profitColor, fontWeight: 500 }}>
                      {isOpen ? '-' : fmt(e.gross_profit)}
                    </td>
                    <td style={{ ...tdStyle, color: profitColor }}>{isOpen ? '-' : fmtPct(e.return_pct)}</td>
                    <td style={tdStyle}>{isOpen ? '-' : (e.position_days || '-')}</td>
                    <td style={{ ...tdStyle, color: profitColor }}>{isOpen ? '-' : fmtPct(e.xirr)}</td>
                    <td style={tdStyle}>{e.ltp_on_expiry ? fmt(e.ltp_on_expiry) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TradingJournal;
